import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../components/api';

const useEvaluation = () => {
  // States principaux
  const [activeTab, setActiveTab] = useState('controles');
  const [showModal, setShowModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedControle, setSelectedControle] = useState(null);

  // Données
  const [controles, setControles] = useState([]);
  const [classes, setClasses] = useState([]);
  const [modules, setModules] = useState([]);
  const [etudiants, setEtudiants] = useState([]);

  // Filtres et sélections
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filtres
  const [filters, setFilters] = useState({
    libelle: '',
    date_debut: '',
    date_fin: '',
    classe: '',
    module: '',
    status_note: ''
  });

  // Données du formulaire
  const [formData, setFormData] = useState({
    libelle: '',
    date_controle: '',
    heure_debut: '',
    heure_fin: '',
    classe: '',
    module: ''
  });

  // Fonction pour charger les contrôles
  const loadControles = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.classe) params.append('classe_id', filters.classe);
      if (filters.module) params.append('module_id', filters.module);

      const response = await api.get(`/controle/controles/?${params}`);
      
      let filteredData = response.data.results || response.data || [];

      // Filtrage côté client pour les autres critères
      if (filters.libelle) {
        filteredData = filteredData.filter(c => 
          c.libelle.toLowerCase().includes(filters.libelle.toLowerCase())
        );
      }

      if (filters.date_debut) {
        filteredData = filteredData.filter(c => c.date_controle >= filters.date_debut);
      }

      if (filters.date_fin) {
        filteredData = filteredData.filter(c => c.date_controle <= filters.date_fin);
      }

      if (filters.status_note !== '') {
        const statusBool = filters.status_note === 'true';
        filteredData = filteredData.filter(c => c.status_note === statusBool);
      }

      setControles(filteredData);
      console.log('✅ Contrôles chargés avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des contrôles:', error);
      if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de charger les évaluations');
      }
      setControles([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fonction pour charger les données de base
  const loadBaseData = useCallback(async () => {
    try {
      const classesRes = await api.get('/classe/get-all-classe/');
      setClasses(classesRes.data.results || classesRes.data || []);
      console.log('✅ Données de base chargées avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données de base:', error);
      if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de charger les données');
      }
    }
  }, []);

  // Fonction pour charger les modules d'une classe
  const loadModulesByClasse = useCallback(async (classeId) => {
    if (!classeId) {
      setModules([]);
      return;
    }
    
    try {
      // D'abord essayer l'API du module controle
      let response;
      try {
        response = await api.get(`/controle/classes/${classeId}/modules/`);
      } catch (error) {
        // Si ça échoue, utiliser l'ancienne API
        console.log('🔄 Tentative avec l\'ancienne API...');
        response = await api.get(`/assiduite/classe/${classeId}/modules/`);
      }
      
      const modules = response.data || [];
      console.log('🔍 Modules reçus:', modules);
      setModules(modules);
      console.log('✅ Modules chargés avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des modules:', error);
      if (error.response?.status === 404) {
        toast.error('Classe introuvable');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de charger les modules');
      }
      setModules([]);
    }
  }, []);

  // Fonction pour charger les étudiants d'une classe
  const loadEtudiantsByClasse = useCallback(async (classeId) => {
    if (!classeId) {
      setEtudiants([]);
      return;
    }
    
    try {
      const response = await api.get(`/assiduite/etudiant-by-class/${classeId}/`);
      setEtudiants(response.data || []);
      console.log('✅ Étudiants chargés avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du chargement des étudiants:', error);
      if (error.response?.status === 404) {
        toast.error('Classe introuvable');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de charger les étudiants');
      }
      setEtudiants([]);
    }
  }, []);

  // Fonction pour soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    if (!formData.libelle || !formData.date_controle || !formData.heure_debut || 
        !formData.heure_fin || !formData.classe || !formData.module) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      let response;
      const data = {
        libelle: formData.libelle,
        date_controle: formData.date_controle,
        heure_debut: formData.heure_debut,
        heure_fin: formData.heure_fin,
        classe: parseInt(formData.classe),
        module: parseInt(formData.module)
      };

      if (isEditing) {
        response = await api.put(`/controle/controles/${formData.id}/`, data);
        toast.success('Contrôle modifié avec succès');
      } else {
        response = await api.post(`/controle/controles/`, data);
        toast.success('Contrôle créé avec succès');
      }

      await loadControles();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('❌ Erreur lors de la soumission:', error);
      
      // Gérer différents types d'erreurs avec des messages conviviaux
      if (error.response?.status === 400) {
        // Erreurs de validation
        if (error.response.data) {
          const errors = error.response.data;
          let errorMessage = 'Données invalides';
          
          if (errors.libelle) {
            errorMessage = 'Type d\'évaluation invalide';
          } else if (errors.date_controle) {
            errorMessage = 'Date du contrôle invalide';
          } else if (errors.heure_debut || errors.heure_fin) {
            errorMessage = 'Horaires invalides';
          } else if (errors.classe) {
            errorMessage = 'Classe non sélectionnée ou invalide';
          } else if (errors.module) {
            errorMessage = 'Module non sélectionné ou invalide';
          } else if (errors.non_field_errors) {
            errorMessage = 'L\'heure de fin doit être après l\'heure de début';
          }
          
          toast.error(errorMessage);
        } else {
          toast.error('Données de formulaire invalides');
        }
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas les permissions nécessaires');
      } else if (error.response?.status === 500) {
        toast.error('Erreur du serveur. Veuillez réessayer plus tard.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion. Vérifiez votre réseau.');
      } else {
        toast.error('Une erreur inattendue s\'est produite');
      }
    }
  };

  // Fonction pour éditer un contrôle
  const handleEdit = (controle) => {
    setFormData({
      id: controle.id,
      libelle: controle.libelle,
      date_controle: controle.date_controle,
      heure_debut: controle.heure_debut,
      heure_fin: controle.heure_fin,
      classe: controle.classe?.id || '',
      module: controle.module?.id || ''
    });
    
    setSelectedClasse(controle.classe?.id || '');
    setSelectedModule(controle.module?.id || '');
    
    // Charger les modules de la classe
    if (controle.classe?.id) {
      loadModulesByClasse(controle.classe.id);
    }
    
    setIsEditing(true);
    setShowModal(true);
  };

  // Fonction pour supprimer un contrôle
  const handleDelete = (controle) => {
    setItemToDelete(controle);
    setShowDeleteModal(true);
  };

  // Confirmation de suppression
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      setIsDeleting(true);
      await api.delete(`/controle/controles/${itemToDelete.id}/`);
      toast.success('Contrôle supprimé avec succès');
      await loadControles();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      if (error.response?.status === 404) {
        toast.error('Évaluation introuvable');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.response?.status === 403) {
        toast.error('Suppression non autorisée');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de supprimer l\'évaluation');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Fonction pour gérer les notes
  const handleManageNotes = (controle) => {
    setSelectedControle(controle);
    loadEtudiantsByClasse(controle.classe?.id);
    setShowNotesModal(true);
  };

  // Fonctions utilitaires
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.slice(0, 5); // HH:MM
  };

  // Reset du formulaire
  const resetForm = () => {
    setFormData({
      libelle: '',
      date_controle: '',
      heure_debut: '',
      heure_fin: '',
      classe: '',
      module: ''
    });
    setSelectedClasse('');
    setSelectedModule('');
    setModules([]);
    setIsEditing(false);
  };

  // Effects
  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    loadControles();
  }, [loadControles]);

  useEffect(() => {
    if (selectedClasse) {
      loadModulesByClasse(selectedClasse);
    } else {
      setModules([]);
    }
  }, [selectedClasse, loadModulesByClasse]);

  return {
    // States
    activeTab,
    showModal,
    showNotesModal,
    showDeleteModal,
    itemToDelete,
    isDeleting,
    isEditing,
    controles,
    loading,
    classes,
    modules,
    etudiants,
    selectedClasse,
    selectedModule,
    currentPage,
    totalPages,
    showFilters,
    filters,
    formData,
    selectedControle,
    
    // Setters
    setActiveTab,
    setShowModal,
    setShowNotesModal,
    setShowDeleteModal,
    setSelectedClasse,
    setSelectedModule,
    setCurrentPage,
    setShowFilters,
    setFilters,
    setFormData,
    setSelectedControle,
    
    // Actions
    handleSubmit,
    handleDelete,
    confirmDelete,
    handleEdit,
    handleManageNotes,
    formatDate,
    formatTime,
    resetForm,
    loadModulesByClasse,
    loadEtudiantsByClasse,
    loadControles
  };
};

export default useEvaluation; 